import { Popover, Spin } from "antd";
import { Link } from "react-router";

import { useScopeParams } from "@/hooks/useScopeParams";
import {
  recordIdFor,
  useDiscrepancyMarkers,
  useRecordDiscrepancyDetail,
  useSubjectDiscrepancySummary,
} from "@/hooks/useDiscrepancyMarkers";
import type {
  DiscrepancyMarker as MarkerData,
  MarkerSeverity,
} from "@/services/discrepancyMarkersApi";

const SEVERITY_COLOR: Record<MarkerSeverity, { fg: string; bg: string }> = {
  Critical: { fg: "#B3372E", bg: "#F9ECEA" },
  Major: { fg: "#B26A00", bg: "#FBF2E2" },
  Minor: { fg: "#6B7F7B", bg: "#EFF2F1" },
};

type DiscrepancyMarkerProps = {
  marker: MarkerData | null;
  /** Subject whose findings the popover lists. */
  subjectId?: string;
  /** Narrow the popover to one visit (visit-level markers). */
  visit?: string;
  /** Hide the count — for markers pinned to a single offending field. */
  countless?: boolean;
  /** Scope the popover to one record instead of the whole participant. */
  entityId?: string;
};

/**
 * The cross-screen indicator: a severity diamond plus the open count, with a
 * popover listing the findings so a record can be triaged without leaving the
 * screen it appears on. Renders nothing when there is no marker, which is what
 * keeps every host screen unchanged while the feature is off.
 */
export default function DiscrepancyMarker({
  marker,
  subjectId,
  visit,
  countless = false,
  entityId,
}: DiscrepancyMarkerProps) {
  if (!marker || marker.open < 1) return null;

  const color = SEVERITY_COLOR[marker.max_severity] ?? SEVERITY_COLOR.Minor;
  const label = `${marker.open} open ${
    marker.open === 1 ? "discrepancy" : "discrepancies"
  }${visit ? ` on ${visit}` : ""}`;

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      content={
        <MarkerPopoverContent
          subjectId={subjectId}
          visit={visit}
          entityId={entityId}
        />
      }
      styles={{ content: { width: 340, padding: 0 } }}
    >
      <button
        type="button"
        aria-label={label}
        title={label}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center gap-1 rounded-full border-0 px-1.5 py-px text-[11px] font-semibold leading-relaxed cursor-pointer"
        style={{ background: color.bg, color: color.fg }}
      >
        <span
          aria-hidden="true"
          className="inline-block h-2 w-2 rotate-45 rounded-[2px]"
          style={{ background: color.fg }}
        />
        {!countless && marker.open}
      </button>
    </Popover>
  );
}

function MarkerPopoverContent({
  subjectId,
  visit,
  entityId,
}: {
  subjectId?: string;
  visit?: string;
  entityId?: string;
}) {
  const { buildUrl } = useScopeParams();
  const subject = useSubjectDiscrepancySummary(
    entityId ? undefined : subjectId,
  );
  const record = useRecordDiscrepancyDetail(entityId);
  const { summary, isLoading } = entityId ? record : subject;

  if (isLoading) {
    return (
      <div className="flex justify-center p-5">
        <Spin size="small" />
      </div>
    );
  }

  const items = (summary?.items ?? []).filter(
    (item) => !visit || item.visit === visit,
  );

  if (!items.length) {
    return (
      <div className="p-4 text-xs text-gray-500">No open discrepancies.</div>
    );
  }

  return (
    <div className="max-h-[320px] overflow-y-auto">
      {items.map((item) => {
        const color = SEVERITY_COLOR[item.severity] ?? SEVERITY_COLOR.Minor;
        return (
          <div
            key={item.discrepancy_id}
            className="border-b border-gray-100 px-4 py-3 last:border-b-0"
          >
            <div className="mb-1 flex items-center gap-2">
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rotate-45 rounded-[2px]"
                style={{ background: color.fg }}
              />
              <span className="text-[13px] font-semibold text-[#003832]">
                {item.rule || "Discrepancy"}
              </span>
              {item.is_new && (
                <span className="rounded bg-[#EAF4F9] px-1.5 py-px text-[10px] font-semibold text-[#06709B]">
                  NEW
                </span>
              )}
            </div>
            {item.detail && (
              <p className="mb-2 rounded border-l-2 border-gray-200 bg-gray-50 px-2.5 py-1.5 text-xs text-gray-600">
                {item.detail}
              </p>
            )}
            <div className="flex items-center justify-between text-[11px] text-gray-500">
              <span>
                {item.severity}
                {item.visit ? ` · ${item.visit}` : ""} · {item.status}
              </span>
              <Link
                className="text-[#06709B] hover:underline"
                to={buildUrl(
                  `data/discrepancies?openIssue=${encodeURIComponent(
                    item.issue_id || item.discrepancy_id,
                  )}`,
                )}
              >
                Open
              </Link>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * A subject-id table cell that carries its own marker.
 *
 * Table cell renderers are plain functions and cannot call hooks, but the JSX
 * they return is rendered by React — so the lookup lives in this component
 * rather than in the renderer's signature. That keeps every table's existing
 * `(value) => ReactNode` contract intact.
 */
export function SubjectCellMarker({ subjectId }: { subjectId: string }) {
  const { lookup } = useDiscrepancyMarkers("subject");
  const marker = lookup(subjectId);
  if (!marker) return null;
  return (
    <span className="ml-1.5 align-middle">
      <DiscrepancyMarker marker={marker} subjectId={subjectId} />
    </span>
  );
}

/**
 * Subject-header marker that resolves its own data, so host screens stay free
 * of the lookup. It carries its own label because the information bar it sits
 * in is label-and-value throughout, and because a label owned by the host
 * screen would remain when a participant has no findings and this returns null.
 */
export function SubjectHeaderMarker({ subjectId }: { subjectId?: string }) {
  const { lookup } = useDiscrepancyMarkers("subject");
  const marker = lookup(subjectId);
  if (!marker) return null;
  return (
    <div className="flex items-center gap-[6px] whitespace-nowrap">
      <span
        className="text-[14px] font-normal tracking-[-0.14px]"
        style={{ color: "rgba(0, 0, 0, 0.45)" }}
      >
        Discrepancies
      </span>
      <DiscrepancyMarker marker={marker} subjectId={subjectId} />
    </div>
  );
}

/**
 * Marker for one row of a data table, scoped to that row's own record.
 *
 * Keys on the record's primary key rather than the participant, so an adverse
 * event carrying a finding is marked while that participant's other events are
 * not — and the popover lists only this record's discrepancies.
 */
export function RecordCellMarker({
  record,
}: {
  record?: Record<string, unknown>;
}) {
  const { lookup } = useDiscrepancyMarkers("record");
  const entityId = recordIdFor(record);
  const marker = lookup(entityId);
  if (!marker || !entityId) return null;
  return (
    <span className="ml-1.5 align-middle">
      <DiscrepancyMarker marker={marker} entityId={entityId} countless />
    </span>
  );
}
